import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { DashboardDateRangeType, DashboardStatsResponse } from '../../types/bookingCreation';

export interface ConfirmationDialogContent {
    headerText: string;
    content: string;
    leftButtonText: string;
    rightButtonText: string;
    // Optional third action, between "abort" and the primary confirm. The
    // cancel-booking dialog uses it to make "with customer email" and "without
    // customer email" two separate commits, so neither can be taken by someone
    // who never looked: there is no pre-set answer to click past. Dialogs that
    // do not set it render exactly as before.
    middleButtonText?: string;
}

interface GlobalSliceState {
    confirmationDialogContent: ConfirmationDialogContent;
    isConfirmationDialog: boolean;
    isLoading: boolean;
    dashboardTab: DashboardDateRangeType;
    dashboardData: DashboardStatsResponse;
    // The last dashboard request failed (#2864). Kept apart from the data so a
    // failure can never be rendered as the initial all-zero figures below.
    dashboardDataFailed: boolean;
    issueReportComment: string;
    // #1379: a request answered 403 `tenant_archived` — the owner closed this
    // account. Holds the tenant slug it was raised for ('' before a tenant is
    // resolved, i.e. on boot); null while nothing was refused. App.tsx swaps
    // the whole UI for the "This account has been closed" screen on it.
    closedTenant: string | null;
}

const initialDashboardData: DashboardStatsResponse = {
    stats: {
        bookings: {
            change: 0,
            current: 0,
            percentageChange: 0,
            previous: 0,
            trend: 'neutral',
        },
        revenues: {
            change: 0,
            current: 0,
            percentageChange: 0,
            previous: 0,
            trend: 'neutral',
        },
    },
    recentBookings: [],
    recentIssueReports: [],
};

const initialState: GlobalSliceState = {
    isLoading: false,
    confirmationDialogContent: {
        content: '',
        headerText: '',
        leftButtonText: '',
        rightButtonText: '',
    },
    isConfirmationDialog: false,
    dashboardTab: 'last7days',
    dashboardData: initialDashboardData,
    dashboardDataFailed: false,
    issueReportComment: '',
    closedTenant: null,
};

const globalSlice = createSlice({
    name: 'global',
    initialState,
    reducers: {
        setConfirmationDialogContent: (state, action: PayloadAction<ConfirmationDialogContent>) => {
            state.confirmationDialogContent = action.payload;
        },
        setIsConfirmationDialog: (state, action: PayloadAction<boolean>) => {
            state.isConfirmationDialog = action.payload;
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setDashboardTab: (state, action: PayloadAction<DashboardDateRangeType>) => {
            state.dashboardTab = action.payload;
        },
        setDashboardData: (state, action: PayloadAction<DashboardStatsResponse>) => {
            state.dashboardData = action.payload;
            state.dashboardDataFailed = false;
        },
        // Drops the previous period's (or tenant's) figures too: after a
        // failure they would otherwise stay on screen as if they were current.
        setDashboardDataFailed: (state) => {
            state.dashboardData = initialDashboardData;
            state.dashboardDataFailed = true;
        },
        setIssueReportComment: (state, action: PayloadAction<string>) => {
            state.issueReportComment = action.payload;
        },
        tenantClosedDetected: (state, action: PayloadAction<string>) => {
            state.closedTenant = action.payload;
        },
    },
});

export const { setConfirmationDialogContent, 
    setDashboardData, 
    setDashboardDataFailed,
    setIsConfirmationDialog, 
    setIsLoading, 
    setDashboardTab,
    setIssueReportComment,
    tenantClosedDetected,
} = globalSlice.actions;

export default globalSlice.reducer;
